import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { Avatar } from '../../components/shared/Avatar';
import { AlertTriangle, Check } from '../../icons';
import { listApprovals, decideReservation, type ReservationDto } from '../../api/reservation';
import { tRsv, tRsvStatus, tRsvDuration, getRsvLang, subscribeRsvLang } from '../../lib/reservation-i18n';
import { dayKey, fmtTime } from './reservation-calendar';
import { conflictingIds, overlapWindow, overlaps, waitingDays } from './reservation-week';
import { ReservationAside } from './ReservationAside';
// 화면 머리 — 근태 · 메일과 같은 정본 A3 (2026-09-08).
import { PageHeader } from '../../components/shared/PageHeader';
import './ReservationPage.css';

/**
 * 승인처리 — 시안 `예약 화면 재설계` 반영 (2026-08-20).
 *
 * 바뀐 것:
 *   · **시간이 겹치는 건을 맨 위로** 올린다. 담당자가 진짜 판단해야 하는 건은 그것뿐이다.
 *   · 여러 건을 골라 한 번에 승인/반려한다(서버 묶음 API 가 없어 건별 호출을 순차로 흘린다).
 *   · 반려 사유 입력칸을 행마다 상시로 두지 않고, 누를 때 뜨는 팝오버 + 자주 쓰는 사유 칩으로 바꾼다.
 *   · 대기 일수("2일째")를 표시해 방치된 건이 눈에 띄게 한다.
 *
 * 이 화면은 담당자에게만 메뉴가 보이지만(서버 manager-scope 판정), 직접 URL 로 들어와도
 * 목록 자체가 담당 자원으로만 필터되고 승인 API 가 담당 여부를 재검증하므로 안전하다.
 */

type ViewFilter = 'pending' | 'done' | 'all';

/** 자주 쓰는 반려 사유 — 누르면 입력칸을 채운다(그대로 보내도 되고 고쳐도 된다). */
const REASON_PRESETS = ['rsv.approve.reason_assigned', 'rsv.approve.reason_over', 'rsv.approve.reason_purpose'] as const;

interface RejectTarget {
  /** 단건이면 그 예약, 묶음이면 null. */
  row: ReservationDto | null;
  ids: string[];
}

export function ReservationApprovalPage() {
  useSyncExternalStore(subscribeRsvLang, getRsvLang, getRsvLang);
  const [pendingRows, setPendingRows] = useState<ReservationDto[]>([]);
  const [doneRows, setDoneRows] = useState<ReservationDto[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [scope, setScope] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyIds, setBusyIds] = useState<string[]>([]);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [view, setView] = useState<ViewFilter>('pending');
  const [resourceFilter, setResourceFilter] = useState('');
  const [checked, setChecked] = useState<string[]>([]);
  const [reject, setReject] = useState<RejectTarget | null>(null);
  const [reason, setReason] = useState('');

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const load = useCallback(async (cursor?: string) => {
    setLoading(true);
    try {
      const res = await listApprovals({ status: 'pending', ...(cursor ? { cursor } : {}), limit: 50 });
      setPendingRows((cur) => (cursor ? [...cur, ...res.reservations] : res.reservations));
      setNextCursor(res.nextCursor);
      setScope(res.managedResourceCount);
      setError(null);
    } catch {
      setError(tRsv('rsv.approve.err'));
    } finally {
      setLoading(false);
    }
  }, []);

  /** 오늘 처리한 건 — 서버가 상태 하나씩만 걸러 주므로 승인/반려를 따로 받아 오늘치만 남긴다. */
  const loadDone = useCallback(async () => {
    try {
      const [ok, no] = await Promise.all([
        listApprovals({ status: 'approved', limit: 30 }),
        listApprovals({ status: 'rejected', limit: 30 }),
      ]);
      const today = dayKey(new Date());
      const rows = [...ok.reservations, ...no.reservations]
        .filter((r) => r.decidedAt && dayKey(new Date(r.decidedAt)) === today)
        .sort((a, b) => new Date(b.decidedAt ?? 0).getTime() - new Date(a.decidedAt ?? 0).getTime());
      setDoneRows(rows);
    } catch {
      // 처리 이력은 부가 정보다 — 실패해도 대기 목록은 그대로 쓴다.
      setDoneRows([]);
    }
  }, []);

  useEffect(() => { void load(); void loadDone(); }, [load, loadDone]);

  // ── 파생 ─────────────────────────────────────────────────────────
  const scoped = useMemo(
    () => (resourceFilter ? pendingRows.filter((r) => r.resourceId === resourceFilter) : pendingRows),
    [pendingRows, resourceFilter],
  );

  const conflicts = useMemo(() => conflictingIds(pendingRows), [pendingRows]);

  const conflictRows = useMemo(
    () => scoped.filter((r) => conflicts.has(r.id)),
    [scoped, conflicts],
  );
  const restRows = useMemo(
    () => scoped.filter((r) => !conflicts.has(r.id)),
    [scoped, conflicts],
  );

  const doneScoped = useMemo(
    () => (resourceFilter ? doneRows.filter((r) => r.resourceId === resourceFilter) : doneRows),
    [doneRows, resourceFilter],
  );

  /** ② 좌측 상세의 담당 자원 목록 — 대기 목록에 등장한 자원에서 뽑는다(자원 목록 API 는 담당 여부를 안 준다). */
  const resourceScopes = useMemo(() => {
    const map = new Map<string, { id: string; name: string; pending: number }>();
    for (const r of [...pendingRows, ...doneRows]) {
      const id = r.resourceId;
      const cur = map.get(id) ?? { id, name: r.resource?.name ?? '-', pending: 0 };
      if (r.status === 'pending') cur.pending += 1;
      map.set(id, cur);
    }
    return [...map.values()].sort((a, b) => b.pending - a.pending || a.name.localeCompare(b.name));
  }, [pendingRows, doneRows]);

  const oldestDays = useMemo(
    () => pendingRows.reduce((max, r) => Math.max(max, waitingDays(r.createdAt, now)), 0),
    [pendingRows, now],
  );

  const checkedRows = useMemo(
    () => pendingRows.filter((r) => checked.includes(r.id)),
    [pendingRows, checked],
  );

  /** 고른 것들끼리 겹치나 — 겹치면 함께 승인할 때 수량을 넘을 수 있다고 먼저 알린다. */
  const checkedHasConflict = useMemo(() => {
    for (let i = 0; i < checkedRows.length; i += 1) {
      for (let j = i + 1; j < checkedRows.length; j += 1) {
        const a = checkedRows[i]!;
        const b = checkedRows[j]!;
        if (a.resourceId === b.resourceId && overlaps(a, b)) return true;
      }
    }
    return false;
  }, [checkedRows]);

  function toggleCheck(id: string) {
    setChecked((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  }

  function conflictPeers(r: ReservationDto): ReservationDto[] {
    return pendingRows.filter((x) => x.id !== r.id && x.resourceId === r.resourceId && overlaps(x, r));
  }

  // ── 처리 ─────────────────────────────────────────────────────────
  async function runDecide(ids: string[], approve: boolean, why: string | null) {
    if (ids.length === 0) return;
    setBusyIds(ids);
    setBulkProgress(ids.length > 1 ? { done: 0, total: ids.length } : null);
    setError(null); setNotice(null);
    let ok = 0;
    let fail = 0;
    let lastMessage: string | null = null;
    for (const id of ids) {
      try {
        await decideReservation(id, approve, approve ? null : why);
        ok += 1;
        setPendingRows((cur) => cur.filter((x) => x.id !== id));
      } catch (e: any) {
        fail += 1;
        lastMessage = e?.response?.data?.message ?? null;
      }
      setBulkProgress((p) => (p ? { ...p, done: p.done + 1 } : null));
    }
    setBusyIds([]);
    setBulkProgress(null);
    setChecked((cur) => cur.filter((x) => !ids.includes(x)));
    if (fail > 0) {
      setError(ids.length > 1
        ? tRsv('rsv.approve.bulk_partial', { ok, fail })
        : (lastMessage ?? tRsv('rsv.approve.action_err')));
    } else if (ids.length > 1) {
      setNotice(tRsv('rsv.approve.bulk_partial', { ok, fail: 0 }));
    }
    void loadDone();
  }

  function openReject(row: ReservationDto | null, ids: string[]) {
    setReason('');
    setReject({ row, ids });
  }

  async function confirmReject() {
    const target = reject;
    if (!target) return;
    setReject(null);
    await runDecide(target.ids, false, reason.trim() === '' ? null : reason.trim());
  }

  const busy = (id: string) => busyIds.includes(id);

  // ── 행 ───────────────────────────────────────────────────────────
  function renderRow(r: ReservationDto, opts: { selectable: boolean; conflict: boolean }) {
    const start = new Date(r.startAt);
    const end = new Date(r.endAt);
    const isDay = (r.resource?.bookingUnit ?? 'time') === 'day';
    const minutes = Math.round((end.getTime() - start.getTime()) / 60000);
    const days = waitingDays(r.createdAt, now);
    const peers = opts.conflict ? conflictPeers(r) : [];
    const others = peers.length;
    // 겹치는 상대가 하나면 **어느 구간이** 겹치는지까지 적는다 — 판단에 그게 필요하다.
    const win = others === 1 ? overlapWindow(r, peers[0]!) : null;
    const decided = r.status === 'approved' || r.status === 'rejected';
    return (
      <div
        key={r.id}
        className={`rsv-item${opts.conflict ? ' is-warn' : ''}`}
      >
        <div className={`rsv-item__stripe ${r.status === 'approved' ? 'is-ok' : r.status === 'rejected' ? 'is-no' : 'is-wait'}`} />
        <div className="rsv-item__b">
          {opts.selectable && (
            <button
              type="button"
              className={`rsv-check${checked.includes(r.id) ? ' is-on' : ''}`}
              onClick={() => toggleCheck(r.id)}
              aria-pressed={checked.includes(r.id)}
              aria-label={tRsv('rsv.approve.select_row')}
            >
              {checked.includes(r.id) && <Check size={10} aria-hidden />}
            </button>
          )}
          <div className="rsv-item__when">
            <b>{start.getMonth() + 1}/{start.getDate()}</b>
            <span>
              {isDay
                ? `${tRsv('rsv.mine.allday')} · ${tRsvDuration(minutes)}`
                : `${fmtTime(start)}–${fmtTime(end)}`}
            </span>
          </div>
          <div className="rsv-item__m">
            <b>
              {r.resource?.name ?? '-'}
              {r.quantity > 1 && <em> · {tRsv('rsv.approve.qty_req', { n: r.quantity })}</em>}
              {r.resource?.location && <em> · {r.resource.location}</em>}
            </b>
            <span className="rsv-item__who">
              <Avatar
                userId={Number(r.requester?.id ?? 0)}
                name={r.requester?.name ?? '-'}
                src={r.requester?.avatarUrl ?? null}
                size="xs"
              />
              {r.requester?.name ?? '-'}
              {r.requester?.position && <em>{r.requester.position}</em>}
              {r.purpose && <span>· {r.purpose}</span>}
            </span>
            {others > 0 && (
              <span className="rsv-conflict">
                <AlertTriangle size={12} aria-hidden />
                {win
                  ? tRsv('rsv.approve.conflict_win', { from: fmtTime(win.start), to: fmtTime(win.end) })
                  : tRsv('rsv.approve.conflict_note', { n: others })}
              </span>
            )}
            {r.status === 'rejected' && r.rejectReason && (
              <span className="rsv-item__reason">{r.rejectReason}</span>
            )}
          </div>
          <div className="rsv-item__a">
            {decided ? (
              <span className={`rsv-pill ${r.status === 'approved' ? 'rsv-pill--ok' : 'rsv-pill--no'}`}>
                {tRsv('rsv.approve.done_at', {
                  label: tRsvStatus(r.status),
                  time: r.decidedAt ? fmtTime(new Date(r.decidedAt)) : '-',
                })}
              </span>
            ) : (
              <>
                <span className="rsv-pill rsv-pill--wait">
                  {days > 0 ? tRsv('rsv.approve.days', { n: days }) : tRsv('rsv.approve.today_new')}
                </span>
                <button
                  type="button"
                  className="rsv-btn rsv-btn--primary rsv-btn--sm"
                  disabled={busy(r.id)}
                  onClick={() => void runDecide([r.id], true, null)}
                >
                  {tRsv('rsv.approve.approve')}
                </button>
                <button
                  type="button"
                  className="rsv-btn rsv-btn--danger rsv-btn--sm"
                  disabled={busy(r.id)}
                  onClick={() => openReject(r, [r.id])}
                >
                  {tRsv('rsv.approve.reject')}
                </button>
              </>
            )}
          </div>
        </div>

        {reject && reject.row?.id === r.id && renderRejectPopover()}
      </div>
    );
  }

  function renderRejectPopover() {
    return (
      <div className="rsv-pop" role="dialog" aria-label={tRsv('rsv.approve.reject_title')}>
        <div className="rsv-pop__h">{tRsv('rsv.approve.reject_title')}</div>
        <div className="rsv-fchips">
          {REASON_PRESETS.map((k) => (
            <button
              key={k}
              type="button"
              className={`rsv-fchip${reason === tRsv(k) ? ' is-on' : ''}`}
              onClick={() => setReason(tRsv(k))}
            >
              {tRsv(k)}
            </button>
          ))}
        </div>
        <input
          className="rsv-input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={tRsv('rsv.approve.reject_reason')}
          aria-label={tRsv('rsv.approve.reject_reason')}
          maxLength={300}
        />
        <div className="rsv-pop__row">
          <button
            type="button"
            className="rsv-btn rsv-btn--danger rsv-btn--sm rsv-btn--grow"
            onClick={() => void confirmReject()}
          >
            {tRsv('rsv.approve.reject_send')}
          </button>
          <button type="button" className="rsv-btn rsv-btn--sm" onClick={() => setReject(null)}>
            {tRsv('rsv.common.cancel')}
          </button>
        </div>
        <div className="rsv-mini">{tRsv('rsv.approve.reject_note')}</div>
      </div>
    );
  }

  const showPending = view !== 'done';
  const showDone = view !== 'pending';

  return (
    <div className="rsv-screen">
      <ReservationAside>
        <div className="rsv-aside__body">
          <div className="rsv-grouphead">
            {tRsv('rsv.approve.scope_head', { n: scope ?? resourceScopes.length })}
          </div>
          <div className={`rsv-res${resourceFilter === '' ? ' is-on' : ''}`}>
            <button type="button" className="rsv-res__pick" onClick={() => setResourceFilter('')}>
              <span className="rsv-res__n">{tRsv('rsv.approve.scope_all')}</span>
              <span className="rsv-res__sub">
                {tRsv('rsv.approve.scope_pending_n', { n: pendingRows.length })}
              </span>
            </button>
          </div>
          {resourceScopes.map((s) => (
            <div key={s.id} className={`rsv-res${resourceFilter === s.id ? ' is-on' : ''}`}>
              <button type="button" className="rsv-res__pick" onClick={() => setResourceFilter(s.id)}>
                <span className="rsv-res__n">{s.name}</span>
                <span className="rsv-res__sub">
                  {s.pending > 0
                    ? tRsv('rsv.approve.scope_pending_n', { n: s.pending })
                    : tRsv('rsv.approve.scope_none')}
                </span>
              </button>
            </div>
          ))}

          <div className="rsv-grouphead">{tRsv('rsv.mine.section_status')}</div>
          <div className="rsv-fchips">
            <button
              type="button"
              className={`rsv-fchip${view === 'pending' ? ' is-on' : ''}`}
              onClick={() => setView('pending')}
            >
              {tRsv('rsv.mine.chip_count', { label: tRsv('rsv.status.pending'), n: pendingRows.length })}
            </button>
            <button
              type="button"
              className={`rsv-fchip${view === 'done' ? ' is-on' : ''}`}
              onClick={() => setView('done')}
            >
              {tRsv('rsv.mine.chip_count', { label: tRsv('rsv.approve.group_done'), n: doneRows.length })}
            </button>
            <button
              type="button"
              className={`rsv-fchip${view === 'all' ? ' is-on' : ''}`}
              onClick={() => setView('all')}
            >
              {tRsv('rsv.approve.scope_all')}
            </button>
          </div>
        </div>
      </ReservationAside>

      <section className="rsv-content">
        {/*
          ★★★ 화면 머리 — **정본 `PageHeader`(A3)** (2026-09-08 사용자 지적).
             예약은 레일 제목(`SecondaryPane`)만 있고 **화면 머리가 통째로 없었다** —
             경로 · 제목 · 한 줄 설명 · 도움말이 다 빠져 있었다.
          ★ 아래 `.rsv-chead` 는 **고른 자원의 머리**라 다른 물건이다 — 그대로 둔다.
        */}
        <PageHeader
          className="rsv-head"
          breadcrumb={[tRsv('rsv.title'), tRsv('rsv.nav.approvals')]}
          title={tRsv('rsv.nav.approvals')}
          description={tRsv('rsv.head.desc_approvals')}
          help={{
            title: tRsv('rsv.head.help_title'),
            body: (
              <>
              <p><b>내가 맡은 자원</b>의 신청만 보입니다 — 담당이 아니면 목록이 비어 있습니다.</p>
              <p>승인하면 그 시간이 잡히고, 반려하면 신청자에게 사유가 갑니다.</p>
              </>
            ),
          }}
        />
        <header className="rsv-chead">
          <div className="rsv-chead__t">
            <strong>{tRsv('rsv.approve.heading')}</strong>
            <span>
              {tRsv('rsv.approve.subtitle', { n: scope ?? 0, p: pendingRows.length })}
              {oldestDays > 0 ? ` · ${tRsv('rsv.approve.oldest', { n: oldestDays })}` : ''}
            </span>
          </div>
          <div className="rsv-chead__sp" />
        </header>

        <div className="rsv-listview scrollbar-thin">
          {error && <p className="rsv-error" role="alert">{error}</p>}
          {notice && <p className="rsv-notice" role="status"><Check size={14} aria-hidden /> {notice}</p>}

          {checked.length > 0 && (
            <div className="rsv-bulkbar">
              <span className="rsv-check is-on" aria-hidden><Check size={10} /></span>
              <b>{tRsv('rsv.approve.selected', { n: checked.length })}</b>
              <span className={checkedHasConflict ? 'rsv-conflict' : 'rsv-bulkbar__note'}>
                {checkedHasConflict && <AlertTriangle size={12} aria-hidden />}
                {checkedHasConflict ? tRsv('rsv.approve.has_conflict') : tRsv('rsv.approve.no_conflict')}
              </span>
              <div className="rsv-chead__sp" />
              {bulkProgress ? (
                <span className="rsv-bulkbar__note">
                  {tRsv('rsv.approve.bulk_busy', { done: bulkProgress.done, total: bulkProgress.total })}
                </span>
              ) : (
                <>
                  <button
                    type="button"
                    className="rsv-btn rsv-btn--primary rsv-btn--sm"
                    onClick={() => void runDecide([...checked], true, null)}
                  >
                    {tRsv('rsv.approve.bulk_approve')}
                  </button>
                  <button
                    type="button"
                    className="rsv-btn rsv-btn--danger rsv-btn--sm"
                    onClick={() => openReject(null, [...checked])}
                  >
                    {tRsv('rsv.approve.bulk_reject')}
                  </button>
                </>
              )}
              {reject && reject.row === null && renderRejectPopover()}
            </div>
          )}

          {loading && pendingRows.length === 0 ? (
            <p className="rsv-empty">{tRsv('rsv.cal.loading')}</p>
          ) : scope === 0 ? (
            <p className="rsv-empty">{tRsv('rsv.approve.no_scope')}</p>
          ) : (
            <>
              {showPending && conflictRows.length > 0 && (
                <div className="rsv-tl">
                  <div className="rsv-tl__h">{tRsv('rsv.approve.group_conflict')}</div>
                  {conflictRows.map((r) => renderRow(r, { selectable: true, conflict: true }))}
                </div>
              )}

              {showPending && restRows.length > 0 && (
                <div className="rsv-tl">
                  <div className="rsv-tl__h">
                    {conflictRows.length > 0 ? tRsv('rsv.approve.group_rest') : tRsv('rsv.status.pending')}
                  </div>
                  {restRows.map((r) => renderRow(r, { selectable: true, conflict: false }))}
                </div>
              )}

              {showPending && scoped.length === 0 && (
                <p className="rsv-empty">{tRsv('rsv.approve.empty')}</p>
              )}

              {showDone && doneScoped.length > 0 && (
                <div className="rsv-tl">
                  <div className="rsv-tl__h">{tRsv('rsv.approve.group_done')}</div>
                  {doneScoped.map((r) => renderRow(r, { selectable: false, conflict: false }))}
                </div>
              )}
            </>
          )}

          {nextCursor && showPending && (
            <button
              type="button"
              className="rsv-btn rsv-more"
              disabled={loading}
              onClick={() => void load(nextCursor)}
            >
              {tRsv('rsv.mine.more')}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
